import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Sidebar from "../components/Sidebar";
import LanguageSelector from "../components/LanguageSelector";
import { getTranslation } from "../i18n/translations";
import { Mail, Send, Loader2 } from "lucide-react";
import { exportTextDocumentToPDF } from "../utils/textDocumentPdf";
import { compressImage } from "../utils/imageCompress";
import { formatMoney } from "../utils/formatCurrency";

const Dashboard = ({ user, logout, lang = 'tr', setLang }) => {
  const navigate = useNavigate();
  const t = (key) => getTranslation(lang, key);
  const [proposals, setProposals] = useState([]);
  const [textDocuments, setTextDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // OCR State
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [ocrImage, setOcrImage] = useState(null);
  const [ocrImagePreview, setOcrImagePreview] = useState(null);
  const [ocrText, setOcrText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const ocrFileInputRef = useRef(null);

  // Email Modal State
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailType, setEmailType] = useState(null); // 'proposal' or 'document'
  const [selectedItem, setSelectedItem] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchProposals();
    fetchTextDocuments();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await api.get("/proposals");
      setProposals(response.data);
    } catch (error) {
      toast.error(t('downloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTextDocuments = async () => {
    try {
      const response = await api.get("/text-documents");
      setTextDocuments(response.data);
    } catch (error) {
      console.error("Text documents fetch error:", error);
    }
  };

  const deleteTextDocument = async (docId) => {
    if (!window.confirm(t('confirmDeleteDocument'))) return;
    try {
      await api.delete(`/text-documents/${docId}`);
      toast.success("Belge silindi");
      fetchTextDocuments();
    } catch (error) {
      toast.error(t('deleteFailed'));
    }
  };

  // Metin belgesi PDF indir (Client-side, WYSIWYG - OCREditor ile birebir)
  const downloadTextDocumentPDF = async (doc) => {
    try {
      toast.info(t('pdfGenerating'));
      await exportTextDocumentToPDF(doc);
      toast.success("PDF indirildi!");
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error("PDF indirilemedi");
    }
  };

  // Metin belgesi yazdır (logo + firma + müşteri bilgileri + antetli kağıt dahil)
  const printTextDocument = async (doc) => {
    try {
      toast.info(t('printPreparing'));
      const { exportTextDocumentToPDF: exportPDF } = await import('../utils/textDocumentPdf');
      await exportPDF(doc, { print: true });
    } catch (err) {
      console.error('Print error:', err);
      toast.error(t('printError') + ': ' + (err.message || t('unknownError')));
    }
  };

  // Email Functions
  const openProposalEmailModal = (proposal) => {
    setEmailType('proposal');
    setSelectedItem(proposal);
    setEmailForm({
      recipient_email: proposal.customer_email || '',
      recipient_name: proposal.customer_name || '',
      subject: `${t('subjectProposalPrefix')}: ${proposal.title || t('untitledProposal')}`,
      message: ''
    });
    setEmailModalOpen(true);
  };

  const openDocumentEmailModal = (doc) => {
    setEmailType('document');
    setSelectedItem(doc);
    setEmailForm({
      recipient_email: '',
      recipient_name: '',
      subject: `Belge: ${doc.name || 'Metin Belgesi'}`,
      message: ''
    });
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error(t('enterRecipientEmail'));
      return;
    }

    setSendingEmail(true);
    try {
      let endpoint;
      let payload;
      
      if (emailType === 'proposal') {
        endpoint = `/proposals/${selectedItem.id}/send-email`;
        payload = {
          proposal_id: selectedItem.id,
          recipient_email: emailForm.recipient_email,
          recipient_name: emailForm.recipient_name,
          subject: emailForm.subject,
          message: emailForm.message,
          lang
        };
      } else {
        endpoint = `/text-documents/${selectedItem.id}/send-email`;
        payload = {
          document_id: selectedItem.id,
          recipient_email: emailForm.recipient_email,
          recipient_name: emailForm.recipient_name,
          subject: emailForm.subject,
          message: emailForm.message
        };
      }
      
      const response = await api.post(endpoint, payload);
      toast.success(response.data.message);
      setEmailModalOpen(false);
      
      if (emailType === 'proposal') {
        fetchProposals(); // Refresh to update status
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('emailSendFailed'));
    } finally {
      setSendingEmail(false);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const total = proposals.length;
    const approved = proposals.filter(p => p.status === 'approved').length;
    const pending = proposals.filter(p => p.status === 'pending' || !p.status).length;
    const draft = proposals.filter(p => p.status === 'draft').length;
    const sent = proposals.filter(p => p.status === 'sent').length;
    const rejected = proposals.filter(p => p.status === 'rejected').length;
    
    // Toplam Gelir: Sadece ONAYLANAN teklifler (müşteri kabul etti)
    const totalRevenue = proposals
      .filter(p => p.status === 'approved')
      .reduce((sum, p) => sum + (p.grand_total || 0), 0);
    
    // Bekleyen Gelir: GÖNDERİLMİŞ teklifler (müşteriden cevap bekleniyor)
    const pendingRevenue = proposals
      .filter(p => p.status === 'sent')
      .reduce((sum, p) => sum + (p.grand_total || 0), 0);
    
    return { total, approved, pending, draft, sent, rejected, totalRevenue, pendingRevenue };
  }, [proposals]);

  // Monthly data for chart
  const monthlyData = useMemo(() => {
    const allMonths = t('monthsShort');
    const currentMonth = new Date().getMonth();
    const months = Array.from({ length: 6 }, (_, i) => allMonths[(currentMonth - 5 + i + 12) % 12]);
    const data = months.map((month, index) => {
      const monthIndex = (currentMonth - 5 + index + 12) % 12;
      const count = proposals.filter(p => {
        const date = new Date(p.created_at);
        return date.getMonth() === monthIndex;
      }).length;
      return { month, count };
    });
    return data;
  }, [proposals]);

  const maxCount = Math.max(...monthlyData.map(d => d.count), 1);

  const deleteProposal = async (id) => {
    if (!window.confirm(t('confirmDeleteProposal'))) return;
    try {
      await api.delete(`/proposals/${id}`);
      toast.success(t('proposalDeleted'));
      fetchProposals();
    } catch (error) {
      toast.error(t('downloadFailed'));
    }
  };

  const updateProposalStatus = async (id, newStatus) => {
    try {
      await api.put(`/proposals/${id}`, { status: newStatus });
      toast.success(t('statusUpdated'));
      fetchProposals();
    } catch (error) {
      toast.error(t('statusUpdateFailed') || "Status update failed");
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: t('draft'),
      pending: t('pending'),
      sent: t('sent'),
      approved: t('approved'),
      rejected: t('rejected')
    };
    return labels[status] || t('pending');
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-400',
      pending: 'bg-amber-500',
      sent: 'bg-blue-500',
      approved: 'bg-green-500',
      rejected: 'bg-red-500'
    };
    return colors[status] || 'bg-amber-500';
  };

  const downloadPDF = async (id, title) => {
    try {
      const response = await api.get(`/proposals/${id}/pdf?lang=${lang}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Her cihazda (masaüstü + mobil) dosya olarak indir
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title || 'teklif'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success(t('pdfDownloaded'));
    } catch (error) {
      toast.error(t('downloadFailed'));
    }
  };

  const printProposal = async (id, title) => {
    try {
      toast.info(t('printPreparing'));
      
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Mobil için PDF'i yeni sekmede aç
        const response = await api.get(`/proposals/${id}/pdf?lang=${lang}`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = url;
          link.target = '_blank';
          link.click();
        }
        toast.info(t('pdfOpenedPrintHint'));
      } else {
        // Desktop için JPG ile yazdırma
        const response = await api.get(`/proposals/${id}/jpg`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'image/jpeg' });
        const url = window.URL.createObjectURL(blob);
        
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(
          '<!DOCTYPE html><html><head><title>' + title + '</title>' +
          '<style>@page{size:A4;margin:0}body{margin:0;padding:0;display:flex;justify-content:center}' +
          'img{width:100%;height:auto;max-width:210mm}</style></head>' +
          '<body><img src="' + url + '" /></body></html>'
        );
        iframeDoc.close();
        
        iframe.onload = function() {
          setTimeout(function() {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(function() {
              document.body.removeChild(iframe);
              window.URL.revokeObjectURL(url);
            }, 1000);
          }, 500);
        };
      }
    } catch (error) {
      toast.error(t('printError') + ": " + (error.message || t('unknownError')));
    }
  };

  // OCR Functions
  const handleOCRImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (Cloudflare/iOS sometimes mis-reports HEIC/HEIF)
    if (file.type && !file.type.startsWith('image/')) {
      toast.error(t('selectImageFile'));
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error(t('fileTooBig'));
      return;
    }

    try {
      // Görseli sıkıştır (uzun kenar 1600px, JPEG q=0.85) — büyük telefon fotoğraflarında network error'u önler
      const { base64, dataUrl } = await compressImage(file, { maxDim: 1600, quality: 0.85 });
      if (!base64) throw new Error('Boş çıktı');
      setOcrImage(base64);
      setOcrImagePreview(dataUrl);
    } catch (err) {
      console.error('Image compression error:', err);
      toast.error(t('imageProcessFailed') + ': ' + (err.message || t('unknownError')));
      // Reset input so user can re-select the same file
      if (ocrFileInputRef.current) ocrFileInputRef.current.value = '';
    }
  };

  const handleOCRScan = async () => {
    if (!ocrImage) {
      toast.error(t('uploadImage'));
      return;
    }
    
    setOcrLoading(true);
    try {
      const response = await api.post('/ocr/extract-text', {
        image_base64: ocrImage
      });
      
      if (response.data.success) {
        setOcrText(response.data.text);
        toast.success(t('scanComplete'));
      } else {
        toast.error(t('scanFailed'));
      }
    } catch (error) {
      console.error('OCR Error:', error);
      toast.error(t('scanFailed') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setOcrLoading(false);
    }
  };

  const handleUseOCRText = () => {
    // Store text in localStorage and navigate to OCR Editor
    localStorage.setItem('ocrExtractedText', ocrText);
    setShowOCRModal(false);
    navigate('/ocr-editor');
  };

  const clearOCR = () => {
    setOcrImage(null);
    setOcrImagePreview(null);
    setOcrText("");
    if (ocrFileInputRef.current) {
      ocrFileInputRef.current.value = "";
    }
  };

  const formatCurrency = (amount) => formatMoney(amount, lang);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30 lg:top-0">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('panel')}</h1>
                <p className="text-slate-500 text-xs sm:text-sm">{t('dashboardOverview')}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
                <Button 
                  onClick={() => setShowOCRModal(true)}
                  variant="outline"
                  size="sm"
                  className="border-purple-500 text-purple-600 hover:bg-purple-50 text-xs sm:text-sm"
                  data-testid="scan-image-button"
                >
                  <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="hidden sm:inline">{t('scanImage')}</span>
                </Button>
                <Button 
                  onClick={() => navigate("/proposal/new")}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm"
                  data-testid="new-proposal-button"
                >
                  <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">{t('newProposal')}</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-4 sm:p-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('totalProposals')}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('approved')}</p>
                  <p className="text-3xl font-bold text-green-500 mt-1">{stats.approved}</p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('awaitingResponse')}</p>
                  <p className="text-3xl font-bold text-amber-500 mt-1">{stats.sent}</p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{t('totalRevenue')}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(stats.totalRevenue)}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Monthly Chart */}
          <Card className="lg:col-span-2 bg-white border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">{t('monthlyProposals')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end justify-between gap-4 pt-4">
                {monthlyData.map((item, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-slate-200 rounded-t-md transition-all duration-300 hover:bg-slate-300 relative group cursor-pointer"
                      style={{ height: `${Math.max((item.count / maxCount) * 180, 8)}px` }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                        {item.month}: {item.count} {t('proposalWord')}
                      </div>
                    </div>
                    <span className="text-xs text-slate-500 mt-2">{item.month}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Status Summary */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">{t('statusSummary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                  <span className="text-sm text-slate-600">{t('draft')}</span>
                </div>
                <span className="font-semibold">{stats.draft}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-slate-600">{t('sent')}</span>
                </div>
                <span className="font-semibold">{stats.sent}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-slate-600">{t('approved')}</span>
                </div>
                <span className="font-semibold">{stats.approved}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-sm text-slate-600">{t('rejected')}</span>
                </div>
                <span className="font-semibold">{stats.rejected}</span>
              </div>
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-slate-500">{t('pendingRevenue')}</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.pendingRevenue)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Proposals */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">{t('recentProposals')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : proposals.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-slate-500 mb-4">{t('noProposalsYet')}</p>
                <Button 
                  onClick={() => navigate("/proposal/new")}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('createFirstProposal')}
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {proposals.slice(0, 5).map((proposal) => (
                  <div key={proposal.id} className="py-4 hover:bg-slate-50 -mx-4 px-4 transition">
                    {/* WEB TARAYICI - md ve üstü ekranlar için */}
                    <div className="hidden md:flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 truncate flex items-center gap-2">
                          {proposal.title}
                          {proposal.is_ocr && (
                            <span className="text-amber-500" title={t('ocrCreated')}>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/>
                              </svg>
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-slate-500">{proposal.customer_name}</span>
                          <span className="text-xs text-slate-400">{formatDate(proposal.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Select value={proposal.status || 'pending'} onValueChange={(value) => updateProposalStatus(proposal.id, value)}>
                          <SelectTrigger className="w-32 h-8 text-xs border-slate-200" data-testid={`status-select-${proposal.id}`}>
                            <SelectValue>
                              <span className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${getStatusColor(proposal.status)}`}></span>
                                {getStatusLabel(proposal.status)}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-slate-400"></span>{t('draft')}</span></SelectItem>
                            <SelectItem value="pending"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500"></span>{t('pending')}</span></SelectItem>
                            <SelectItem value="sent"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span>{t('sent')}</span></SelectItem>
                            <SelectItem value="approved"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>{t('approved')}</span></SelectItem>
                            <SelectItem value="rejected"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500"></span>{t('rejected')}</span></SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="font-semibold text-slate-900 ml-2 mr-2">{formatCurrency(proposal.grand_total || 0)}</span>
                        <Button data-testid={`edit-proposal-${proposal.id}`} onClick={() => navigate(`/proposal/${proposal.id}`)} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700" title={t('edit')}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </Button>
                        <Button data-testid={`email-proposal-${proposal.id}`} onClick={() => openProposalEmailModal(proposal)} variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-700" title={t('sendEmailTitle')}>
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button data-testid={`download-pdf-${proposal.id}`} onClick={() => downloadPDF(proposal.id, proposal.title)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700" title="PDF">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9v4h2v-4h2l-3-3-3 3h2z"/></svg>
                        </Button>
                        <Button data-testid={`print-${proposal.id}`} onClick={() => printProposal(proposal.id, proposal.title)} variant="ghost" size="sm" className="text-teal-500 hover:text-teal-700" title={t('print')}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        </Button>
                        <Button data-testid={`delete-${proposal.id}`} onClick={() => deleteProposal(proposal.id)} variant="ghost" size="sm" className="text-slate-400 hover:text-red-500" title={t('delete')}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </Button>
                      </div>
                    </div>
                    
                    {/* MOBİL/APP - md altı ekranlar için */}
                    <div className="md:hidden">
                      {/* Satır 1: Başlık + Durum + Fiyat */}
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
                          {proposal.title}
                          {proposal.is_ocr && (
                            <span className="text-amber-500" title={t('ocrCreated')}>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/>
                              </svg>
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center gap-2">
                          <Select value={proposal.status || 'pending'} onValueChange={(value) => updateProposalStatus(proposal.id, value)}>
                            <SelectTrigger className="w-28 h-8 text-xs border-slate-200" data-testid={`status-mobile-${proposal.id}`}>
                              <SelectValue>
                                <span className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${getStatusColor(proposal.status)}`}></span>
                                  {getStatusLabel(proposal.status)}
                                </span>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-slate-400"></span>{t('draft')}</span></SelectItem>
                              <SelectItem value="pending"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500"></span>{t('pending')}</span></SelectItem>
                              <SelectItem value="sent"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span>{t('sent')}</span></SelectItem>
                              <SelectItem value="approved"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>{t('approved')}</span></SelectItem>
                              <SelectItem value="rejected"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500"></span>{t('rejected')}</span></SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="font-bold text-slate-900">{formatCurrency(proposal.grand_total || 0)}</span>
                        </div>
                      </div>
                      {/* Satır 2: Müşteri + Tarih */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-sm text-slate-500">{proposal.customer_name}</span>
                        <span className="text-xs text-slate-400">{formatDate(proposal.created_at)}</span>
                      </div>
                      {/* Satır 3: Butonlar */}
                      <div className="flex items-center gap-6">
                        <Button data-testid={`edit-mobile-${proposal.id}`} onClick={() => navigate(`/proposal/${proposal.id}`)} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 p-0" title={t('edit')}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </Button>
                        <Button data-testid={`email-mobile-${proposal.id}`} onClick={() => openProposalEmailModal(proposal)} variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-700 p-0" title={t('sendEmailTitle')}>
                          <Mail className="w-5 h-5" />
                        </Button>
                        <Button data-testid={`pdf-mobile-${proposal.id}`} onClick={() => downloadPDF(proposal.id, proposal.title)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 p-0" title="PDF">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9v4h2v-4h2l-3-3-3 3h2z"/></svg>
                        </Button>
                        <Button data-testid={`print-mobile-${proposal.id}`} onClick={() => printProposal(proposal.id, proposal.title)} variant="ghost" size="sm" className="text-teal-500 hover:text-teal-700 p-0" title={t('print')}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        </Button>
                        <Button data-testid={`delete-mobile-${proposal.id}`} onClick={() => deleteProposal(proposal.id)} variant="ghost" size="sm" className="text-slate-400 hover:text-red-500 p-0" title={t('delete')}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metin Belgeleri */}
        {textDocuments.length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Metin Belgeleri
                <span className="text-sm font-normal text-slate-500">({textDocuments.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {textDocuments.slice(0, 5).map((doc) => (
                  <div key={doc.id} className="py-4 hover:bg-slate-50 -mx-4 px-4 transition">
                    {/* WEB TARAYICI - md ve üstü ekranlar için */}
                    <div className="hidden md:flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 truncate flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {doc.name}
                        </h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-slate-500 truncate max-w-xs">{doc.content?.substring(0, 50)}...</span>
                          <span className="text-xs text-slate-400">{new Date(doc.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <Button onClick={() => navigate(`/ocr-editor/${doc.id}`)} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 p-2" title={t('edit')}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </Button>
                        <Button onClick={() => openDocumentEmailModal(doc)} variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-700 p-2" title={t('sendEmailTitle')}>
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => downloadTextDocumentPDF(doc)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 p-2" title={t('downloadPdf')}>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9v4h2v-4h2l-3-3-3 3h2z"/></svg>
                        </Button>
                        <Button onClick={() => printTextDocument(doc)} variant="ghost" size="sm" className="text-teal-500 hover:text-teal-700 p-2" title={t('print')}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                        </Button>
                        <Button onClick={() => deleteTextDocument(doc.id)} variant="ghost" size="sm" className="text-slate-400 hover:text-red-500 p-2" title="Sil">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </Button>
                      </div>
                    </div>
                    
                    {/* MOBİL - md altı ekranlar için */}
                    <div className="md:hidden">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {doc.name}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-500 truncate mb-2">{doc.content?.substring(0, 40)}...</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">{new Date(doc.created_at).toLocaleDateString('tr-TR')}</span>
                        <div className="flex gap-1">
                          <Button onClick={() => navigate(`/ocr-editor/${doc.id}`)} variant="ghost" size="sm" className="text-slate-500 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </Button>
                          <Button onClick={() => openDocumentEmailModal(doc)} variant="ghost" size="sm" className="text-emerald-500 p-1">
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => downloadTextDocumentPDF(doc)} variant="ghost" size="sm" className="text-red-500 p-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9v4h2v-4h2l-3-3-3 3h2z"/></svg>
                          </Button>
                          <Button onClick={() => printTextDocument(doc)} variant="ghost" size="sm" className="text-teal-500 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                          </Button>
                          <Button onClick={() => deleteTextDocument(doc.id)} variant="ghost" size="sm" className="text-slate-400 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        </main>
      </div>

      {/* OCR Modal */}
      <Dialog open={showOCRModal} onOpenChange={setShowOCRModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('scanImage')}
            </DialogTitle>
            <p className="text-sm text-slate-500">{t('scanImageDesc')}</p>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Image Upload Area */}
            <div 
              className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-purple-400 transition cursor-pointer"
              onClick={() => ocrFileInputRef.current?.click()}
            >
              {ocrImagePreview ? (
                <div className="relative">
                  <img 
                    src={ocrImagePreview} 
                    alt="Preview" 
                    className="max-h-64 mx-auto rounded-lg"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); clearOCR(); }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="py-8">
                  <svg className="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-slate-600 font-medium">{t('uploadImage')}</p>
                  <p className="text-sm text-slate-400 mt-1">PNG, JPG, JPEG - Max 10MB</p>
                </div>
              )}
              <input
                type="file"
                ref={ocrFileInputRef}
                onChange={handleOCRImageSelect}
                accept="image/png,image/jpeg,image/jpg"
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
                data-testid="ocr-image-input"
              />
            </div>

            {/* Scan Button */}
            {ocrImage && !ocrText && (
              <Button
                onClick={handleOCRScan}
                disabled={ocrLoading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6"
              >
                {ocrLoading ? (
                  <>
                    <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('scanning')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    {t('scanImage')} (AI)
                  </>
                )}
              </Button>
            )}

            {/* Extracted Text Editor */}
            {ocrText && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('extractedText')}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearOCR}
                    className="text-slate-500"
                  >
                    {t('clearText')}
                  </Button>
                </div>
                <Textarea
                  value={ocrText}
                  onChange={(e) => setOcrText(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                  placeholder={t('extractedText')}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => { clearOCR(); }}
                    variant="outline"
                    className="flex-1"
                  >
                    {t('scanImage')} (Yeni)
                  </Button>
                  <Button
                    onClick={handleUseOCRText}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('useThisText')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              {emailType === 'proposal' ? t('sendProposal') : t('sendDocument')}
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.title || selectedItem?.name || t('subjectDocumentPrefix')} — {t('sendByEmailDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient_email">{t('recipientEmail')} *</Label>
              <Input
                id="recipient_email"
                type="email"
                placeholder="ornek@email.com"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                data-testid="dashboard-email-recipient"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient_name">{t('recipientName')}</Label>
              <Input
                id="recipient_name"
                placeholder={t('recipientName')}
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Konu</Label>
              <Input
                id="subject"
                placeholder="E-posta konusu"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Mesaj (Opsiyonel)</Label>
              <Textarea
                id="message"
                placeholder={t('messagePlaceholder')}
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                rows={4}
              />
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-800">
                <strong>{t('senderLabel')}:</strong> info@nktteklif.com
              </p>
              {(() => {
                try {
                  const s = JSON.parse(localStorage.getItem('nkt_company_settings') || '{}');
                  const rt = (s.sender_email || '').trim();
                  return rt ? (
                    <p className="text-xs text-emerald-700 mt-1">
                      ↩︎ {t('replyToLabel') || 'Yanıtlar buraya gelir'}: <strong>{rt}</strong>
                    </p>
                  ) : null;
                } catch { return null; }
              })()}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              disabled={sendingEmail}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.recipient_email}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="dashboard-send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('sending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('sendBtn')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
